import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetError,
  UseFormSetValue,
} from 'react-hook-form';
import clsx from 'clsx';
import flatpickr from 'flatpickr';
import {
  convertPathToDotNotation,
  getPageLocale,
  handleKeyDown,
  isMyMscArea,
  langLocaleMap,
  replacePlaceholders,
  RootState,
  yearDropdownPlugin,
} from '@msc-b2c/shared-utils';
import { Icon } from '../../icons/Icon';
import { useSelector } from 'react-redux';
import { GuestType } from '@msc-b2c/interfaces/types';
import styles from './datepicker.module.scss';

type DatePickerInputProps = {
  label: string;
  name: string;
  index: number;
  control: Control<any>;
  errors: FieldErrors;
  clearErrors: UseFormClearErrors<any>;
  setError: UseFormSetError<any>;
  setValue: UseFormSetValue<any>;
  disabled?: boolean;
  isLowerLabel?: boolean;
  validation?: any;
  inputTypeDate?: string;
  disableCheckAgePopup?: boolean;
  styleOptions?: string;
  guestType?: GuestType;
  minAge: number | undefined;
  invalidDateErrorMessage?: string;
  dateValues?: string;
};

export const DatePickerInput = ({
  label,
  name,
  index,
  control,
  errors,
  clearErrors,
  setError,
  setValue,
  disabled,
  isLowerLabel,
  validation,
  guestType,
  minAge,
  invalidDateErrorMessage,
  inputTypeDate,
  disableCheckAgePopup,
  styleOptions = '',
  dateValues,
}: DatePickerInputProps) => {
  const { areInfantsAllowed, ageRestriction } = useSelector((state: RootState) => state.cruiseData);
  const noInfantsAllowed = areInfantsAllowed !== undefined && !areInfantsAllowed;

  const dateInputRef = useRef(null);

  const isError = convertPathToDotNotation(errors, name);
  const isErrorClass = isError ? 'error-input' : '';
  const isDisabled = disabled ? 'disabled-input' : '';
  const typeDate = inputTypeDate ? inputTypeDate : label;
  const labelClass = isLowerLabel
    ? 'cmp-input__lowerlabel'
    : (noInfantsAllowed && guestType === GuestType.MINOR) ||
      (ageRestriction.minimumAge !== undefined &&
        ageRestriction?.minimumAge !== 18 &&
        guestType === GuestType.ADULT)
    ? 'cmp-input__ageRestriction'
    : 'cmp-input__upperlabel';

  const checkErrorMessageTooLong = (errorMessage: Element) => {
    const lineHeight = parseFloat(getComputedStyle(errorMessage).lineHeight);
    const height = errorMessage.clientHeight;
    if (height > lineHeight) {
      return true;
    }
    return false;
  };

  // check if we have an error message whick is too long
  useEffect(() => {
    if (dateInputRef.current) {
      const errorElement = (dateInputRef.current as HTMLElement).querySelector(
        '.error-list-validation > div'
      );
      const isLongErrorText = errorElement ? checkErrorMessageTooLong(errorElement) : false;

      // if we have the error and error text is too long add a margin
      if (isLongErrorText) {
        (dateInputRef.current as HTMLElement).classList.add(styles['cmp-form-text__long-error']);
      } else {
        (dateInputRef.current as HTMLElement).classList.remove(styles['cmp-form-text__long-error']);
      }
    }
  }, [isError]);

  return (
    <div className={`text ${labelClass} ${isErrorClass} ${isDisabled} ${styleOptions}`}>
      <div
        className="cmp-form-text"
        ref={dateInputRef}
      >
        {!isLowerLabel && (
          <label
            className={clsx('cmp-label', isErrorClass)}
            htmlFor={`${name}-${index}`}
          >
            {label}
          </label>
        )}
        <Controller
          name={name}
          control={control}
          rules={validation}
          render={({ field }) => (
            <FlatpickrInputWrapper
              dateValues={dateValues}
              field={field}
              name={name}
              index={index}
              clearErrors={clearErrors}
              setError={setError}
              setValue={setValue}
              isError={!!isError}
              disabled={disabled}
              label={typeDate}
              errors={errors}
              disableCheckAgePopup={disableCheckAgePopup}
              minAge={minAge}
              invalidDateErrorMessage={invalidDateErrorMessage}
            />
          )}
        />
        {isError && (
          <div className="error-list-validation">
            <div>{isError.message?.toString()}</div>
          </div>
        )}
      </div>
    </div>
  );
};

type FlatpickrInputWrapperProps = {
  field: any;
  name: string;
  index: number;
  clearErrors: UseFormClearErrors<any>;
  setError: UseFormSetError<any>;
  setValue: UseFormSetValue<any>;
  isError: boolean;
  disabled?: boolean;
  label: string;
  errors: FieldErrors;
  disableCheckAgePopup?: boolean;
  minAge?: number;
  invalidDateErrorMessage?: string;
  dateValues?: string;
};

const FlatpickrInputWrapper = ({
  field,
  name,
  index,
  clearErrors,
  setError,
  setValue,
  isError,
  disabled,
  label,
  errors,
  disableCheckAgePopup,
  minAge,
  invalidDateErrorMessage,
  dateValues,
}: FlatpickrInputWrapperProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const flatpickrInstance = useRef<any>(null); // Reference to Flatpickr instance
  const { adultsOccupancy, childrenOccupancy, infantOccupancy, date } = useSelector(
    (state: RootState) => state.bookingParams
  );

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const minDateRef = useRef<Date | null>(null);
  const maxDateRef = useRef<Date | null>(null);
  const minDateIsoRef = useRef<string | null>(null);
  const maxDateIsoRef = useRef<string | null>(null);

  // Convert "DD/MM/YYYY" -> "YYYY-MM-DD" using string parsing only (no Date/timezone/dayjs).
  const displayToIsoDate = (value?: string | null): string | null => {
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec((value ?? '').trim());
    if (!match) return null;
    const [, dd, mm, yyyy] = match;
    const month = Number(mm);
    const day = Number(dd);
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    return `${yyyy}-${mm}-${dd}`;
  };

  // Format date for display (YYYY-MM-DD to DD/MM/YYYY)
  const formatDateForDisplay = (value: string | null) => {
    if (!value) return '';
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return value;

    let out = '';
    const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (isoMatch) {
      const [, yyyy, mm, dd] = isoMatch;
      out = `${dd}/${mm}/${yyyy}`;
    } else {
      const date = dayjs(value);
      out = date.isValid() ? date.format('DD/MM/YYYY') : '';
    }
    return out;
  };

  useEffect(() => {
    // Set the input value to display format when field value changes
    if (inputRef.current && field.value) {
      inputRef.current.value = formatDateForDisplay(field.value);
    } else if (inputRef.current && !field.value) {
      inputRef.current.value = '';
    }

    // Only blur if THIS input is currently focused.
    // Dont steal focus back from the next field
    setTimeout(() => {
      if (!inputRef.current) return;
      if (!inputRef.current.value) return;

      if (document.activeElement === inputRef.current) {
        inputRef.current.blur();
      }
    });
  }, [field.value]);

  useEffect(() => {
    if (inputRef.current) {
      const today = dayjs().utc(true);
      let sailingDate = today;
      if (date) {
        const sailingDateString = date.split('-')[0];
        if (sailingDateString) sailingDate = dayjs.utc(sailingDateString, 'DD/MM/YYYY');
      }
      const eighteenYearsAgo = dayjs(sailingDate).subtract(minAge ?? 18, 'year');
      const twoYearsBeforeSailingPlusOneDay = dayjs(sailingDate).subtract(2, 'year').add(1, 'day');
      const twoYearsBeforeSailing = dayjs(sailingDate).subtract(2, 'year');
      const adultsCount = adultsOccupancy ?? 0; // Default to 0 if undefined
      const childrenCount = childrenOccupancy ?? 0; // Default to 0 if undefined
      const infantCount = infantOccupancy ?? 0;

      // Determine maxDate based on age group
      let selectedYear = today;
      let maxDate = today;
      let minDate = today.add(50, 'year');
      let paxType;

      if (isMyMscArea()) {
        //This scenario is for use the date picker outside of booking funnel
        paxType = '';
      } else {
        //This scenario is for use the date picker inside the booking funnel
        // Determine the paxType based on the index
        if (index < adultsCount) {
          paxType = 'Adult';
        } else if (adultsCount <= index && index < adultsCount + childrenCount) {
          paxType = 'Minor';
        } else {
          paxType = 'Infant';
        }
      }

      if (name.includes('dateOfBirth')) {
        // Apply date restrictions based on paxType
        if (paxType === 'Adult') {
          minDate = dayjs(maxDate).subtract(100, 'year');
          maxDate = eighteenYearsAgo;
        } else if (paxType === 'Minor') {
          minDate = dayjs(sailingDate).subtract(18, 'year').add(1, 'day');
          maxDate = twoYearsBeforeSailing;
        } else if (paxType === 'Infant') {
          minDate = twoYearsBeforeSailingPlusOneDay;
          maxDate = today;
        } else {
          minDate = dayjs(today).subtract(100, 'year');
          maxDate = dayjs(today).subtract(minAge ?? 18, 'year');
        }
        selectedYear = maxDate;
      }

      if (
        (name.includes('guest.dateOfBirthAsString') && adultsCount <= 0) ||
        name.includes('dateOfBirthAsString')
      ) {
        minDate = dayjs(today).subtract(100, 'year');
        maxDate = dayjs(today);
      }

      if (name.includes('signInDetails')) {
        minDate = dayjs(today);
        maxDate = dayjs(today);
        selectedYear = today;
      }

      if (name.includes('pregnancy')) {
        minDate = dayjs(today);
        maxDate = dayjs(today).add(9, 'month');
      }

      if (name.includes('dateOfExpire')) {
        maxDate = dayjs(today).add(10, 'year');
        minDate = dayjs(today).add(1, 'day');
        selectedYear = today;
      }

      if (name.includes('passportDateOfIssue')) {
        maxDate = dayjs(today);
        minDate = dayjs(today).subtract(10, 'year');
        selectedYear = today;
      }

      if (name === 'customer.dateOfBirth') {
        if (disableCheckAgePopup) {
          maxDate = dayjs(today).subtract(18, 'year');
        } else {
          maxDate = dayjs(today);
        }

        minDate = dayjs(maxDate).subtract(100, 'year');
      }

      minDateRef.current = minDate.toDate();
      maxDateRef.current = maxDate.toDate();
      minDateIsoRef.current = minDate.format('YYYY-MM-DD');
      maxDateIsoRef.current = maxDate.format('YYYY-MM-DD');

      const instance = flatpickr(inputRef.current, {
        plugins: [
          yearDropdownPlugin({
            selectedYear: selectedYear.format('DD/MM/YYYY'),
            yearStart: minDate.year(),
            yearEnd: maxDate.year(),
          }),
        ],
        dateFormat: 'd/m/Y',
        altIntput: true,
        allowInput: true,
        clickOpens: true,
        disableMobile: true,
        closeOnSelect: true,
        shorthandCurrentMonth: true,
        monthSelectorType: 'dropdown',
        allowInvalidPreload: true,
        locale: langLocaleMap[getPageLocale()],
        maxDate: maxDate.format('DD/MM/YYYY'),
        minDate: minDate.format('DD/MM/YYYY'),
        onChange: (selectedDates) => {
          const selectedDate = selectedDates[0];
          field.onChange(dayjs(selectedDate).format('YYYY-MM-DD'));
          validateDate(selectedDate);
          setIsCalendarOpen(false);
          if (!selectedDate) {
            setValue(name, maxDate.toDate());
            if (inputRef.current) {
              inputRef.current.value = maxDate.toString();
            }
          }
        },
        onClose: (selectedDates) => {
          // Only handle blur and error, no calendar sync here
          field.onBlur(inputRef.current?.value);
          setIsCalendarOpen(false);
        },
        onReady: (_selectedDates, _dateStr, instance) => {
          instance.calendarContainer.classList.add(`${name.split('.')[1]}-${index}`);
          const monthDropdown = instance.monthsDropdownContainer;
          if (monthDropdown) {
            monthDropdown.addEventListener('click', () => {
              monthDropdown.childNodes.forEach((option, index) => {
                if (option instanceof HTMLElement) {
                  option.textContent =
                    instance.l10n.months.shorthand[
                      parseInt(option.getAttribute('value') as string) ?? index
                    ];
                }
              });
            });
            monthDropdown.addEventListener('mousedown', () => {
              monthDropdown.childNodes.forEach((option, index) => {
                if (option instanceof HTMLElement) {
                  option.textContent =
                    instance.l10n.months.shorthand[
                      parseInt(option.getAttribute('value') as string) ?? index
                    ];
                }
              });
            });

            monthDropdown.addEventListener('mouseout', () => {
              monthDropdown.childNodes.forEach((option, index) => {
                if (option instanceof HTMLElement) {
                  option.textContent =
                    instance.l10n.months.shorthand[
                      parseInt(option.getAttribute('value') as string) ?? index
                    ];
                }
              });
            });
            monthDropdown.addEventListener('change', () => {
              monthDropdown.childNodes.forEach((option, index) => {
                if (option instanceof HTMLElement) {
                  option.textContent =
                    instance.l10n.months.shorthand[
                      parseInt(option.getAttribute('value') as string) ?? index
                    ];
                }
              });
            });
          }
          setIsCalendarOpen(true);
        },
        onOpen: (selectedDates, _dateStr, instance) => {
          let dateToShow = selectedDates[0] || null;
          if (!dateToShow) return;
          if (inputRef.current?.value) {
            const parsedDate = dayjs(inputRef.current.value, 'DD/MM/YYYY', true);
            if (
              parsedDate.isValid() &&
              minDateRef.current &&
              maxDateRef.current &&
              parsedDate.isAfter(dayjs(minDateRef.current).subtract(1, 'day')) &&
              parsedDate.isBefore(dayjs(maxDateRef.current).add(1, 'day'))
            ) {
              dateToShow = parsedDate.toDate();
            } else {
              // if date is not in range max and mindate then do not assing it the max date
              dateToShow = maxDateRef.current;
            }
          }
          if (flatpickrInstance.current && dateToShow) {
            flatpickrInstance.current.jumpToDate(dateToShow);
          }
          if (selectedDates[0] && !isCalendarOpen) {
            instance.close();
            setIsCalendarOpen(true);
          }
        },
      });

      flatpickrInstance.current = instance; // Store the instance

      const handleScroll = () => {
        const calendar = document.querySelector('.flatpickr-calendar') as HTMLElement;
        if (
          calendar &&
          calendar.style.display !== 'none' &&
          calendar.getAttribute('aria-hidden') !== 'true' &&
          flatpickrInstance.current.isOpen
        ) {
          instance.close();
        }
      };

      document.body.addEventListener('scroll', handleScroll);
      return () => {
        document.body.removeEventListener('scroll', handleScroll);
      };
    }
  }, [name, isCalendarOpen]);

  const handleBlur = () => {
    const inputValue = inputRef.current?.value?.trim();

    setValue(name, inputValue, { shouldValidate: false });

    if (!inputValue) {
      field.onBlur();
      return;
    }

    // Parse + compare entirely as YYYY-MM-DD strings (no Date/dayjs/timezone), so it is
    // stable regardless of the browser timezone or environment.
    const isoDate = displayToIsoDate(inputValue);
    const minIso = minDateIsoRef.current;
    const maxIso = maxDateIsoRef.current;
    const inRange = !!isoDate && (!minIso || isoDate >= minIso) && (!maxIso || isoDate <= maxIso);

    // Special handling for signInDetails - must be exactly today (no auto-correction; show error so user enters it manually)
    if (name.includes('signInDetails')) {
      const todayIso = dayjs().format('YYYY-MM-DD');

      if (!isoDate || isoDate !== todayIso) {
        field.onChange('');
        if (inputRef.current) {
          inputRef.current.value = '';
        }
        setError(name, {
          type: 'manual',
          message: replacePlaceholders(invalidDateErrorMessage ?? '', {
            fieldName: label,
          }),
        });
        field.onBlur();
        return;
      }
    }

    if (inRange && isoDate) {
      // Valid date
      clearErrors(name);
      field.onChange(isoDate);
      if (flatpickrInstance.current && !flatpickrInstance.current.isOpen) {
        flatpickrInstance.current.setDate(inputValue, false);
        flatpickrInstance.current.jumpToDate(inputValue);
      }
    } else {
      // Invalid date
      setError(name, {
        type: 'manual',
        message: replacePlaceholders(invalidDateErrorMessage ?? 'Invalid date', {
          fieldName: label,
        }),
      });
    }

    field.onBlur();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement> & { nativeEvent: InputEvent }) => {
    const target = e.target as HTMLInputElement;
    const rawDigits = target.value.replace(/\D/g, ''); // Rimuove tutto tranne i numeri
    const isBackspace = e.nativeEvent.inputType === 'deleteContentBackward';
    let cursorPosition = target.selectionStart || 0; // Posizione attuale del cursore
    let formattedValue = '';

    for (let i = 0; i < rawDigits.length && i < 8; i++) {
      if (i === 2 || i === 4) {
        formattedValue += '/';
        if (cursorPosition === i + 1 && !isBackspace) {
          cursorPosition++;
        }
      }
      formattedValue += rawDigits[i];
    }

    target.value = formattedValue;

    if (isBackspace) {
      if (cursorPosition === 3 || cursorPosition === 6) {
        cursorPosition--;
      } else if (cursorPosition === 5) {
        cursorPosition = 4;
      }
    } else if (cursorPosition === 3 || cursorPosition === 6) {
      cursorPosition++;
    }
    target.setSelectionRange(cursorPosition, cursorPosition);
  };

  const validateDate = (date: Date | null) => {
    const requiredErrorMessage = invalidDateErrorMessage ?? 'Invalid date';
    if (!date || isNaN(date.getTime())) {
      setError(name, {
        type: 'required',
        message: replacePlaceholders(requiredErrorMessage, { fieldName: label }),
      });
      return;
    }
    clearErrors(name);
  };

  // Open calendar on icon click
  const handleIconClick = () => {
    flatpickrInstance.current.open();
  };

  // Close calendar if browser's back button is pushed
  window.addEventListener('popstate', () => flatpickrInstance.current.close());

  return (
    <div className="date-picker-input-wrapper">
      <input
        data-date-values={dateValues ?? ''}
        ref={inputRef}
        name={name}
        autoComplete="off"
        id={`${name}-${index}`}
        type="text"
        inputMode="numeric" // Show numeric keyboard on mobile devices
        className={clsx('cmp-form-text__text form-control input active', {
          'error-input': convertPathToDotNotation(errors, name),
        })}
        placeholder={label}
        disabled={disabled}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={() => clearErrors(name)}
        aria-invalid={isError ? 'true' : 'false'}
        defaultValue={formatDateForDisplay(field.value)}
      />
      <span
        className="calendar-icon"
        onClick={handleIconClick}
        onKeyDown={(event) => handleKeyDown(event, handleIconClick)}
        style={{
          position: 'absolute',
          top: '50%', // Center vertically
          right: '2px',
          backgroundColor: 'white',
          transform: 'translateY(-50%)', // Correct for the vertical alignment
          cursor: 'pointer', // Change cursor to pointer on hover
          fontSize: '1.5em', // Size of the icon; adjust as needed
          pointerEvents: 'all', // Ensure it captures click events
          zIndex: 1, // Bring the icon in front of other elements if necessary
          color: '#555', // Change the icon color; adjust as needed
        }}
      >
        <Icon icon={'calendar'}></Icon>
      </span>
    </div>
  );
};
